// src/hooks/useBBQScore.js

import { useMemo } from 'react';
import { BBQ_SCORE_WEIGHTS, TEMP_THRESHOLDS_F } from '../constants';
import { getLocalHour } from '../utils';

const calculateBBQScoreInternal = (currentWeather) => {
  if (!currentWeather || typeof currentWeather.temperature === 'undefined') {
    return 0;
  }

  const localHour = getLocalHour(currentWeather.timezone);

  let totalScore = 0;

  // Temperature Score
  let tempScore = 0;
  const tempC = currentWeather.temperature;
  const tempF = (tempC * 9/5) + 32;
  if (tempF >= TEMP_THRESHOLDS_F.IDEAL_MIN && tempF <= TEMP_THRESHOLDS_F.IDEAL_MAX) { tempScore = 100; }
  else if ((tempF >= TEMP_THRESHOLDS_F.GOOD_MIN && tempF < TEMP_THRESHOLDS_F.IDEAL_MIN) || (tempF > TEMP_THRESHOLDS_F.IDEAL_MAX && tempF <= TEMP_THRESHOLDS_F.GOOD_MAX)) { tempScore = 75; }
  else if ((tempF >= TEMP_THRESHOLDS_F.OK_MIN && tempF < TEMP_THRESHOLDS_F.GOOD_MIN) || (tempF > TEMP_THRESHOLDS_F.GOOD_MAX && tempF <= TEMP_THRESHOLDS_F.OK_MAX)) { tempScore = 50; }
  else { tempScore = 10; }
  totalScore += tempScore * BBQ_SCORE_WEIGHTS.TEMPERATURE;

  // Precipitation Score
  let precipScore = 0;
  const rainProb = currentWeather.rainProbability;
  if (currentWeather.condition === "Rain" || currentWeather.condition === "Snow" || currentWeather.condition === "Drizzle" || rainProb >= 50) { precipScore = 0; }
  else if (rainProb > 10 && rainProb < 50) { precipScore = 50; }
  else { precipScore = 100; }
  totalScore += precipScore * BBQ_SCORE_WEIGHTS.PRECIPITATION;

  // Wind Score
  let windScore = 0;
  const windKmh = currentWeather.windSpeed * 3.6;
  if (windKmh <= 15) { windScore = 100; }
  else if (windKmh > 15 && windKmh <= 30) { windScore = 50; }
  else { windScore = 10; }
  totalScore += windScore * BBQ_SCORE_WEIGHTS.WIND;

  // Cloud Score
  let cloudScore = 0;
  const condition = currentWeather.condition;
  const description = currentWeather.description ? currentWeather.description.toLowerCase() : '';
  if (condition === "Clear") { cloudScore = 100; }
  else if (condition === "Clouds") {
    if (description.includes("few clouds") || description.includes("scattered clouds")) { cloudScore = 80; }
    else if (description.includes("broken clouds") || description.includes("overcast clouds")) { cloudScore = 60; }
    else { cloudScore = 70; }
  } else { cloudScore = 30; }
  totalScore += cloudScore * BBQ_SCORE_WEIGHTS.CLOUD;

  // Night penalty (11 PM – 8 AM): halve the score rather than reducing to 20%
  if (localHour >= 23 || localHour < 8) {
    totalScore *= 0.5;
  }

  return Math.round(Math.max(0, Math.min(totalScore, 100)));
};

export const useBBQScore = (weatherData) => {
  const bbqScore = useMemo(() => {
    if (!weatherData) return 0;
    return calculateBBQScoreInternal(weatherData);
  }, [weatherData]);

  return { bbqScore };
};